import React from "react";
import EventAttributeDateSourcePicker from "./EventAttributeDateSourcePicker";
import EventAttributeDateSourceListPicker from "./EventAttributeDateSourceListPicker";

/**
 * Shared "Object Type / Reference Date / Actual Date" settings block, ported
 * verbatim from LiefertermintreueKPISettings.jsx so both new timeline tiles
 * use the exact same date-source configuration (independent ref/actual date
 * types, actual date as a multi-select list with "earliest wins") — plus a
 * third "Event-Attribut" source (a custom field on events of a given
 * activity, e.g. "requested_delivery_date" on an order-creation event),
 * which LiefertermintreueKPI does not support.
 */
const DeliveryDateConfigSection = ({
  objectType,
  onObjectTypeChange,
  objectTypes,
  refDateType,
  onRefDateTypeChange,
  refDateValue,
  onRefDateValueChange,
  actualDateType,
  onActualDateTypeChange,
  actualDateValue,
  onActualDateValueChange,
  refEventAttributeSource,
  onRefEventAttributeSourceChange,
  actualEventAttributeSources,
  onActualEventAttributeSourcesChange,
  getAttributesForActivity,
  activitiesForType,
  dateAttrsForType,
  loading = false,
  activitiesLoading = false,
  dateAttrsLoading = false,
  refSectionLabel = "Soll-Termin (Referenzdatum)",
  actualSectionLabel = "Ist-Termin (tatsächliches Datum)",
}) => {
  const objectTypeList = Array.isArray(objectTypes) ? objectTypes : Object.keys(objectTypes || {});

  const renderDateValueSelect = (dateType, dateValue, onDateValueChange) => {
    const isDisabled = loading || !objectType;
    if (dateType === "activity_timestamp") {
      return (
        <select
          value={dateValue}
          onChange={(e) => onDateValueChange(e.target.value)}
          disabled={isDisabled || activitiesLoading}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          {!objectType ? (
            <option value="">Erst Objekttyp wählen…</option>
          ) : (
            <option value="">Aktivität wählen…</option>
          )}
          {activitiesForType.map((act) => (
            <option key={act} value={act}>{act}</option>
          ))}
        </select>
      );
    }
    return (
      <select
        value={dateValue}
        onChange={(e) => onDateValueChange(e.target.value)}
        disabled={isDisabled || dateAttrsLoading}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        {!objectType ? (
          <option value="">Erst Objekttyp wählen…</option>
        ) : dateAttrsLoading ? (
          <option value="">Lade Attribute…</option>
        ) : dateAttrsForType.length === 0 ? (
          <option value="">Keine Datumsattribute gefunden</option>
        ) : (
          <option value="">Datumsattribut wählen…</option>
        )}
        {dateAttrsForType.map((attr) => (
          <option key={attr} value={attr}>{attr}</option>
        ))}
      </select>
    );
  };

  return (
    <>
      {/* Object Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Objekttyp *</label>
        <select
          value={objectType}
          onChange={(e) => onObjectTypeChange(e.target.value)}
          disabled={loading}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Objekttyp wählen…</option>
          {objectTypeList.map((ot) => (
            <option key={ot} value={ot}>{ot}</option>
          ))}
        </select>
      </div>

      {/* Reference (Soll) Date */}
      <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-800">{refSectionLabel}</h4>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Quelle *</label>
          <select
            value={refDateType}
            onChange={(e) => onRefDateTypeChange(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="object_attribute">Objekt-Attribut</option>
            <option value="activity_timestamp">Aktivitäts-Zeitstempel</option>
            <option value="event_attribute">Event-Attribut</option>
          </select>
        </div>
        {refDateType === "event_attribute" ? (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Aktivität + Event-Attribut *</label>
            <EventAttributeDateSourcePicker
              value={refEventAttributeSource}
              onChange={onRefEventAttributeSourceChange}
              activitiesForType={activitiesForType}
              getAttributesForActivity={getAttributesForActivity}
              loading={loading || !objectType}
              activitiesLoading={activitiesLoading}
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {refDateType === "activity_timestamp" ? "Aktivität *" : "Attribut *"}
            </label>
            {renderDateValueSelect(refDateType, refDateValue, onRefDateValueChange)}
          </div>
        )}
      </div>

      {/* Actual (Ist) Date */}
      <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-semibold text-gray-800">{actualSectionLabel}</h4>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Quelle *</label>
          <select
            value={actualDateType}
            onChange={(e) => onActualDateTypeChange(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="object_attribute">Objekt-Attribut</option>
            <option value="activity_timestamp">Aktivitäts-Zeitstempel</option>
            <option value="event_attribute">Event-Attribut</option>
          </select>
        </div>
        {actualDateType === "event_attribute" ? (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Aktivität(en) + Event-Attribut(e) *</label>
            <EventAttributeDateSourceListPicker
              values={actualEventAttributeSources}
              onChange={onActualEventAttributeSourcesChange}
              activitiesForType={activitiesForType}
              getAttributesForActivity={getAttributesForActivity}
              loading={loading || !objectType}
              activitiesLoading={activitiesLoading}
            />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              {actualDateType === "activity_timestamp" ? "Aktivität(en) *" : "Attribut(e) *"}
            </label>
            {(() => {
              const isDisabled = loading || !objectType;
              const isLoading = actualDateType === "activity_timestamp" ? activitiesLoading : dateAttrsLoading;
              const items = actualDateType === "activity_timestamp" ? activitiesForType : dateAttrsForType;
              return (
                <div
                  className="w-full border border-gray-300 rounded-md overflow-y-auto h-28"
                  style={{ opacity: (isDisabled || isLoading) ? 0.5 : 1, pointerEvents: (isDisabled || isLoading) ? "none" : "auto" }}
                >
                  {!objectType && <p className="px-3 py-2 text-sm text-gray-400">Erst Objekttyp wählen…</p>}
                  {objectType && isLoading && <p className="px-3 py-2 text-sm text-gray-400">Lädt…</p>}
                  {objectType && !isLoading && items.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">Keine Optionen gefunden</p>}
                  {items.map((item) => (
                    <div
                      key={item}
                      onClick={() => {
                        const next = actualDateValue.includes(item)
                          ? actualDateValue.filter((a) => a !== item)
                          : [...actualDateValue, item];
                        onActualDateValueChange(next);
                      }}
                      className={`px-3 py-1.5 cursor-pointer text-sm select-none ${actualDateValue.includes(item) ? "bg-primary-500 text-white" : "hover:bg-gray-100"}`}
                    >
                      {item}
                    </div>
                  ))}
                </div>
              );
            })()}
            <p className="text-xs text-gray-500 mt-1">Mehrere Attribute auswählbar. Frühestes Datum wird verwendet.</p>
          </div>
        )}
      </div>
    </>
  );
};

export default DeliveryDateConfigSection;
